import { useMemo } from 'react';
import { Network, Boxes } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { EmptyState, StatusPill } from '@vnetwork/ui/data';
import { Card, CardBody, Badge } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useLoadBalancerStats, type LoadBalancer } from '@/api/opennebula';
import { DescList, DescItem, monoValue } from '../../components/DescList';

/** Map a backend host:port to its live status from the stats feed. */
function useServerStatus(serviceId: string, lbId: string) {
  const { data: groups = [] } = useLoadBalancerStats(serviceId, lbId);
  return useMemo(() => {
    const m = new Map<string, string>();
    for (const g of groups) {
      for (const s of g.values) {
        if (s.title !== 'Server' || !s.name) continue;
        // server name format: lb<n>_<ip>_<port>
        const parts = s.name.split('_');
        const port = parts[parts.length - 1];
        const ip = parts[parts.length - 2];
        if (ip && port) m.set(`${ip}:${port}`, s.status ?? '');
      }
    }
    return m;
  }, [groups]);
}

function tone(s?: string): 'success' | 'danger' | 'muted' {
  const v = (s ?? '').toUpperCase();
  if (v === 'UP') return 'success';
  if (v === 'DOWN') return 'danger';
  return 'muted';
}

/**
 * Ingress / Kubernetes view. When a load balancer is managed by a Kubernetes
 * cluster (`metadata.cluster.id`), it fronts the cluster's ingress controller —
 * listeners are read-only routing rules. This tab shows the cluster linkage plus
 * each listener as an ingress route with live backend health.
 */
export function LbIngress({ serviceId, lb }: { serviceId: string; lb: LoadBalancer }) {
  const cluster = lb.metadata?.cluster;
  const listeners = lb.metadata?.listeners ?? [];
  const status = useServerStatus(serviceId, lb.id);

  return (
    <Section
      title="Ingress"
      description={cluster?.id ? 'Routing managed by the Kubernetes ingress controller.' : 'Routing rules derived from listeners.'}
    >
      {cluster?.id && (
        <Card style={{ marginBottom: 'var(--vn-space-4)' }}>
          <CardBody>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', marginBottom: 'var(--vn-space-3)' }}>
              <Boxes size={18} />
              <strong>Kubernetes cluster</strong>
              <Badge tone="info" appearance="subtle" size="sm">Managed</Badge>
            </div>
            <DescList style={{ gap: 'var(--vn-space-3)' }}>
              <DescItem label="Cluster">{cluster.name || '—'}</DescItem>
              <DescItem label="Cluster ID"><code style={monoValue}>{cluster.id}</code></DescItem>
              {cluster.version && <DescItem label="Version">{cluster.version}</DescItem>}
            </DescList>
            <Alert tone="info" title="Read-only" style={{ marginTop: 'var(--vn-space-3)' }}>
              Listeners are provisioned by the cluster's ingress controller and update automatically with your Ingress resources.
            </Alert>
          </CardBody>
        </Card>
      )}

      {listeners.length === 0 ? (
        // Populated state is a custom Card grid — wrap empty in Card so both
        // states share a consistent bordered frame.
        <Card><CardBody><EmptyState icon={<Network size={28} />} title="No ingress routes" description="This load balancer has no listeners configured." /></CardBody></Card>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {listeners.map((l, i) => (
            <Card key={i}>
              <CardBody>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', marginBottom: 'var(--vn-space-2)' }}>
                  <Badge tone={l.protocol === 'HTTPS' ? 'success' : 'info'} appearance="subtle" size="sm">{l.protocol}</Badge>
                  <strong>Port {l.port}</strong>
                  {l.certificate && <Badge tone="neutral" appearance="subtle" size="sm">TLS</Badge>}
                </div>
                {(l.servers?.length ?? 0) === 0 ? (
                  <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>No backends.</p>
                ) : (
                  <div style={{ display: 'grid', gap: 4 }}>
                    {l.servers!.map((s, si) => {
                      const st = status.get(`${s.host}:${s.port}`);
                      return (
                        <div key={si} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 'var(--vn-font-size-sm)' }}>
                          <span>{s.host}:{s.port}</span>
                          <StatusPill tone={tone(st)}>{st || 'unknown'}</StatusPill>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </Section>
  );
}

